import { useState, useEffect, useContext, useRef } from 'react';
import {
    Box, Typography, Stack, Accordion, AccordionGroup, AccordionDetails,
    AccordionSummary, Button, IconButton, Chip, Sheet, Alert,
    accordionSummaryClasses,
    accordionDetailsClasses,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    Select,
    Option,
    FormControl,
    FormLabel,
    LinearProgress,
    List,
    ListItem,
    ListItemContent,
    CircularProgress,
    Input,
    Textarea,
    Switch,
    Table,
    Divider
} from '@mui/joy';
import { LayoutContext } from '../context';
import { APIGetAdminCategoriesTree, APICreateCategory, APIUpdateCategory, APIDeleteCategory, APICreateQuestion, APIGetAllQuestions, APIGetQuestionForEdit, APIUpdateQuestion, APIDeleteQuestion, APIImportQuestionsCSV, APIBulkUpdateQuestionsCategory, APIReorderCategories, APIAnalyzeDuplicates, APIGetDuplicates, APIClearDuplicates, APIHideAllDuplicates, APIHideDuplicate, APIUnmarkDuplicate, APIAdminGetPlans, APIAdminCreatePlan, APIAdminUpdatePlan, APIAdminDeletePlan, APIGetAdminReviews } from '../api';
import { Star, Plus, PencilSimple, Trash, CaretDown, CaretRight, Folder, Exam, UploadSimple, CheckCircle, WarningCircle, FolderOpen, EyeSlash } from "@phosphor-icons/react";
import Checkbox from '@mui/joy/Checkbox';
import CategoryModal from '../components/modals/CategoryModal';
import QuestionModal from '../components/modals/QuestionModal';
import { Modal, ModalDialog, ModalClose, DialogTitle, DialogContent } from '@mui/joy';

export default function AdminPage() {
    const { token, user } = useContext(LayoutContext);
    const [categories, setCategories] = useState([]);
    const [allQuestions, setAllQuestions] = useState([]);
    const [questionsByCategory, setQuestionsByCategory] = useState({});
    const [loading, setLoading] = useState(true);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    // Модальные окна
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [questionModalOpen, setQuestionModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [parentCategory, setParentCategory] = useState(null);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // CSV Import
    const [csvFile, setCsvFile] = useState(null);
    const [csvImportCategory, setCsvImportCategory] = useState(null);
    const [csvImporting, setCsvImporting] = useState(false);
    const [csvImportResult, setCsvImportResult] = useState(null);
    const csvFileInputRef = useRef(null);

    // Batch selection
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [bulkCategoryId, setBulkCategoryId] = useState(null);
    const [bulkUpdating, setBulkUpdating] = useState(false);

    // Move question modal
    const [moveQuestionModalOpen, setMoveQuestionModalOpen] = useState(false);
    const [questionToMove, setQuestionToMove] = useState(null);
    const [targetCategoryId, setTargetCategoryId] = useState(null);
    const [movingQuestion, setMovingQuestion] = useState(false);

    // Flagged questions from localStorage
    const [flaggedQuestions, setFlaggedQuestions] = useState(() => {
        const saved = localStorage.getItem('flaggedQuestions');
        return saved ? JSON.parse(saved) : [];
    });
    const [deletingFlagged, setDeletingFlagged] = useState(false);

    // Duplicate analysis
    const [duplicates, setDuplicates] = useState([]);
    const [duplicatesLoading, setDuplicatesLoading] = useState(false);
    const [analyzingDuplicates, setAnalyzingDuplicates] = useState(false);
    const [duplicatesPagination, setDuplicatesPagination] = useState(null);
    const [duplicatesError, setDuplicatesError] = useState('');
    const [selectedCategoriesForAnalysis, setSelectedCategoriesForAnalysis] = useState([]);
    const [analyzedCategories, setAnalyzedCategories] = useState(() => {
        const saved = localStorage.getItem('analyzedCategories');
        return saved ? JSON.parse(saved) : [];
    });
    const [expandedDupCategories, setExpandedDupCategories] = useState([]);

    // Study Plans
    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [planForm, setPlanForm] = useState({
        title: '',
        description: '',
        cover_emoji: '',
        is_published: false,
        weeks: []
    });
    const [planSaving, setPlanSaving] = useState(false);

    // Reviews
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        await Promise.all([
            fetchCategories(),
            fetchAllQuestions()
        ]);
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await APIGetAdminCategoriesTree(token);
            if (response.success) {
                setCategories(response.categories);
            } else {
                setError(response.error);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            setError('Ошибка при загрузке категорий');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllQuestions = async () => {
        try {
            setQuestionsLoading(true);
            const response = await APIGetAllQuestions(token);
            if (response.success) {
                setAllQuestions(response.questions);
                distributeQuestionsByCategory(response.questions);
            } else {
                setError(response.error);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            setError('Ошибка при загрузке вопросов');
        } finally {
            setQuestionsLoading(false);
        }
    };
    
    // Распределяем вопросы по категориям
    const distributeQuestionsByCategory = (questions) => {
        const distributed = {};
        
        questions.forEach(question => {
            question.categories.forEach(category => {
                if (!distributed[category.id]) {
                    distributed[category.id] = [];
                }
                distributed[category.id].push(question);
            });
        });
        
        setQuestionsByCategory(distributed);
    };

    const handleCreateCategory = (parentCategory = null) => {
        setParentCategory(parentCategory);
        setEditingCategory(null);
        setCategoryModalOpen(true);
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setParentCategory(null);
        setCategoryModalOpen(true);
    };

    const handleCreateQuestion = (category = null) => {
        setSelectedCategory(category);
        setEditingQuestion(null);
        setQuestionModalOpen(true);
    };

    const handleDeleteCategory = async (category) => {
        const hasQuestions = category.question_count > 0;
        const message = hasQuestions
            ? `Удалить категорию "${category.name}" и все ${category.question_count} вопросов в ней? Это действие нельзя отменить!`
            : `Удалить категорию "${category.name}"?`;

        if (!window.confirm(message)) {
            return;
        }

        try {
            const response = await APIDeleteCategory(token, category.id);
            if (response.success) {
                fetchData(); // Обновляем все данные
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            alert('Ошибка при удалении категории');
        }
    };

    const handleEditQuestion = async (question) => {
        try {
            const response = await APIGetQuestionForEdit(token, question.id);
            if (response.success) {
                setEditingQuestion(response.question);
                setSelectedCategory(null);
                setQuestionModalOpen(true);
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error fetching question for edit:', error);
            alert('Ошибка при загрузке вопроса');
        }
    };

    const handleDeleteQuestion = async (question) => {
        try {
            await APIDeleteQuestion(token, question.id);
            fetchData();
        } catch (error) {
            console.error('Error deleting question:', error);
        }
    };

    // Открыть модалку для перемещения вопроса
    const handleOpenMoveQuestion = (question) => {
        setQuestionToMove(question);
        setTargetCategoryId(null);
        setMoveQuestionModalOpen(true);
    };

    // Переместить вопрос в другую категорию
    const handleMoveQuestion = async () => {
        if (!questionToMove || !targetCategoryId) return;

        setMovingQuestion(true);
        try {
            const response = await APIBulkUpdateQuestionsCategory(
                token,
                [questionToMove.id],
                [targetCategoryId],
                'replace'
            );
            if (response.success) {
                setMoveQuestionModalOpen(false);
                setQuestionToMove(null);
                setTargetCategoryId(null);
                fetchData();
            } else {
                alert(response.error || 'Ошибка при перемещении вопроса');
            }
        } catch (error) {
            console.error('Error moving question:', error);
            alert('Ошибка при перемещении вопроса');
        } finally {
            setMovingQuestion(false);
        }
    };

    // Получить плоский список всех категорий для Select
    const getFlatCategories = (cats, level = 0) => {
        let result = [];
        cats.forEach(cat => {
            result.push({
                ...cat,
                displayName: '—'.repeat(level) + ' ' + cat.name,
                level
            });
            if (cat.children && cat.children.length > 0) {
                result = result.concat(getFlatCategories(cat.children, level + 1));
            }
        });
        return result;
    };

    const flatCategories = getFlatCategories(categories);

    const handleSaveCategory = async (categoryData) => {
        try {
            let response;
            if (editingCategory) {
                response = await APIUpdateCategory(token, editingCategory.id, categoryData);
            } else {
                const data = parentCategory 
                    ? { ...categoryData, parent: parentCategory.id }
                    : categoryData;
                response = await APICreateCategory(token, data);
            }

            if (response.success) {
                setCategoryModalOpen(false);
                fetchData(); // Обновляем все данные
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error saving category:', error);
            alert('Ошибка при сохранении категории');
        }
    };

    const handleSaveQuestion = async (questionData) => {
        try {
            let response;
            if (editingQuestion) {
                response = await APIUpdateQuestion(token, editingQuestion.id, questionData);
            } else {
                response = await APICreateQuestion(token, questionData);
            }

            if (response.success) {
                setQuestionModalOpen(false);
                fetchData(); // Обновляем все данные
            } else {
                alert(response.error);
            }
        } catch (error) {
            console.error('Error saving question:', error);
            alert('Ошибка при сохранении вопроса');
        }
    };

    // Функция для рекурсивного поиска всех вопросов в категории и ее подкатегориях
    const getAllQuestionsInCategory = (category) => {
        let questions = [];

        // Добавляем вопросы непосредственно в этой категории
        if (questionsByCategory[category.id]) {
            questions = [...questionsByCategory[category.id]];
        }

        // Рекурсивно добавляем вопросы из подкатегорий
        if (category.children) {
            category.children.forEach(child => {
                questions = [...questions, ...getAllQuestionsInCategory(child)];
            });
        }

        return questions;
    };

    // Функция для получения плоского списка категорий
    const flattenCategories = (cats, prefix = '') => {
        let result = [];
        cats.forEach(cat => {
            result.push({
                id: cat.id,
                name: prefix + cat.name,
                fullPath: prefix + cat.name
            });
            if (cat.children && cat.children.length > 0) {
                result = [...result, ...flattenCategories(cat.children, prefix + cat.name + ' → ')];
            }
        });
        return result;
    };

    // CSV Import handlers
    const handleCsvFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setCsvFile(file);
            setCsvImportResult(null);
        }
    };

    // Batch selection handlers
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedQuestions(allQuestions.map(q => q.id));
        } else {
            setSelectedQuestions([]);
        }
    };

    const handleSelectQuestion = (questionId, checked) => {
        if (checked) {
            setSelectedQuestions(prev => [...prev, questionId]);
        } else {
            setSelectedQuestions(prev => prev.filter(id => id !== questionId));
        }
    };

    const handleBulkUpdateCategory = async () => {
        if (selectedQuestions.length === 0) {
            alert('Выберите вопросы для обновления');
            return;
        }

        if (bulkCategoryId === null) {
            alert('Выберите категорию');
            return;
        }

        setBulkUpdating(true);
        try {
            const response = await APIBulkUpdateQuestionsCategory(
                token,
                selectedQuestions,
                bulkCategoryId ? [bulkCategoryId] : [],
                'replace'
            );

            if (response.success) {
                alert(`Обновлено ${response.updated_count} вопросов`);
                setSelectedQuestions([]);
                setBulkCategoryId(null);
                fetchData();
            } else {
                alert('Ошибка: ' + response.error);
            }
        } catch (error) {
            console.error('Bulk update error:', error);
            alert('Ошибка при обновлении');
        } finally {
            setBulkUpdating(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedQuestions.length === 0) return;

        try {
            for (const questionId of selectedQuestions) {
                await APIDeleteQuestion(token, questionId);
            }
            setSelectedQuestions([]);
            fetchData();
        } catch (error) {
            console.error('Bulk delete error:', error);
        }
    };

    const handleDeleteFlagged = async () => {
        if (flaggedQuestions.length === 0) return;

        setDeletingFlagged(true);
        try {
            for (const questionId of flaggedQuestions) {
                await APIDeleteQuestion(token, questionId);
            }
            // Clear localStorage
            localStorage.removeItem('flaggedQuestions');
            setFlaggedQuestions([]);
            fetchData();
        } catch (error) {
            console.error('Delete flagged error:', error);
        } finally {
            setDeletingFlagged(false);
        }
    };

    const handleClearFlags = () => {
        localStorage.removeItem('flaggedQuestions');
        setFlaggedQuestions([]);
    };

    const handleCsvImport = async () => {
        if (!csvFile) {
            alert('Выберите CSV файл');
            return;
        }

        setCsvImporting(true);
        setCsvImportResult(null);

        try {
            const categoryIds = csvImportCategory ? [csvImportCategory] : [];
            const response = await APIImportQuestionsCSV(token, csvFile, categoryIds);

            setCsvImportResult(response);

            if (response.success) {
                // Обновляем данные
                fetchData();
                // Очищаем файл
                setCsvFile(null);
                if (csvFileInputRef.current) {
                    csvFileInputRef.current.value = '';
                }
            }
        } catch (error) {
            console.error('CSV import error:', error);
            setCsvImportResult({
                success: false,
                error: error?.response?.data?.error || 'Ошибка при импорте CSV'
            });
        } finally {
            setCsvImporting(false);
        }
    };

    // ===== Duplicate Analysis Functions =====

    const loadDuplicates = async (page = 1) => {
        setDuplicatesLoading(true);
        setDuplicatesError('');
        try {
            const response = await APIGetDuplicates(token, page, 50);
            if (response.success) {
                setDuplicates(response.duplicates);
                setDuplicatesPagination(response.pagination);
            } else {
                setDuplicatesError(response.error || 'Ошибка загрузки');
            }
        } catch (error) {
            console.error('Load duplicates error:', error);
            setDuplicatesError('Ошибка загрузки дубликатов');
        } finally {
            setDuplicatesLoading(false);
        }
    };

    const handleAnalyzeDuplicates = async () => {
        if (selectedCategoriesForAnalysis.length === 0) {
            alert('Выберите хотя бы одну категорию для анализа');
            return;
        }

        if (!window.confirm(`Запустить анализ ${selectedCategoriesForAnalysis.length} категорий? Это может занять несколько минут.`)) return;

        setAnalyzingDuplicates(true);
        setDuplicatesError('');
        try {
            const response = await APIAnalyzeDuplicates(token, 50, selectedCategoriesForAnalysis);
            if (response.success) {
                // Проверяем, были ли ошибки во всех батчах
                if (response.errors && response.errors.length > 0 && response.total_duplicates === 0) {
                    const errorMsg = response.errors[0];
                    setDuplicatesError(`Ошибка API: ${errorMsg}`);
                    alert(`Ошибка при анализе!\n${errorMsg}\n\nВсего ошибок: ${response.errors.length}`);
                } else {
                    let msg = `Анализ завершён!\n${response.message}`;
                    if (response.errors && response.errors.length > 0) {
                        msg += `\n\nОшибки в ${response.errors.length} батчах (остальные обработаны)`;
                    }
                    alert(msg);

                    // Отмечаем категории как проанализированные
                    const newAnalyzed = [...new Set([...analyzedCategories, ...selectedCategoriesForAnalysis])];
                    setAnalyzedCategories(newAnalyzed);
                    localStorage.setItem('analyzedCategories', JSON.stringify(newAnalyzed));

                    // Сбрасываем выбор
                    setSelectedCategoriesForAnalysis([]);

                    loadDuplicates();
                }
            } else {
                console.error('API error:', response);
                setDuplicatesError(response.error || 'Ошибка анализа');
            }
        } catch (error) {
            console.error('Analyze duplicates error:', error);
            console.error('Error response:', error?.response?.data);
            const errorMsg = error?.response?.data?.error || error?.message || 'Ошибка при анализе дубликатов';
            setDuplicatesError(errorMsg);
        } finally {
            setAnalyzingDuplicates(false);
        }
    };

    const handleToggleCategoryForAnalysis = (categoryId) => {
        setSelectedCategoriesForAnalysis(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
    };

    const handleClearAnalyzedCategories = () => {
        setAnalyzedCategories([]);
        localStorage.removeItem('analyzedCategories');
    };

    const handleClearDuplicateFlags = async () => {
        if (!window.confirm('Снять все метки дубликатов?')) return;

        try {
            const response = await APIClearDuplicates(token);
            if (response.success) {
                alert(response.message);
                setDuplicates([]);
                setDuplicatesPagination(null);
            }
        } catch (error) {
            console.error('Clear duplicates error:', error);
            alert('Ошибка при очистке меток');
        }
    };

    const handleHideAllDuplicates = async () => {
        if (!window.confirm(`Скрыть ВСЕ ${duplicatesPagination?.total_count || 0} дубликатов? Вопросы останутся в базе, но не будут появляться в тестах.`)) return;

        try {
            const response = await APIHideAllDuplicates(token);
            if (response.success) {
                alert(response.message);
                setDuplicates([]);
                setDuplicatesPagination(null);
                fetchData(); // Refresh questions count
            }
        } catch (error) {
            console.error('Hide all duplicates error:', error);
            alert('Ошибка при скрытии дубликатов');
        }
    };

    const handleUnmarkDuplicate = async (questionId) => {
        try {
            const response = await APIUnmarkDuplicate(token, questionId);
            if (response.success) {
                setDuplicates(prev => prev.filter(d => d.id !== questionId));
                if (duplicatesPagination) {
                    setDuplicatesPagination(prev => ({
                        ...prev,
                        total_count: prev.total_count - 1
                    }));
                }
            }
        } catch (error) {
            console.error('Unmark duplicate error:', error);
            alert('Ошибка при снятии метки');
        }
    };

    const handleHideDuplicate = async (questionId) => {
        try {
            const response = await APIHideDuplicate(token, questionId);
            if (response.success) {
                setDuplicates(prev => prev.filter(d => d.id !== questionId));
                if (duplicatesPagination) {
                    setDuplicatesPagination(prev => ({
                        ...prev,
                        total_count: prev.total_count - 1
                    }));
                }
            }
        } catch (error) {
            console.error('Hide duplicate error:', error);
            alert('Ошибка при скрытии');
        }
    };

    // Load duplicates when tab changes to duplicates
    useEffect(() => {
        if (activeTab === 3 && duplicates.length === 0 && !duplicatesLoading) {
            loadDuplicates();
        }
    }, [activeTab]);

    // Load plans when tab changes to plans
    useEffect(() => {
        if (activeTab === 4 && plans.length === 0 && !plansLoading) {
            loadPlans();
        }
    }, [activeTab]);

    // Load reviews when tab changes to reviews
    useEffect(() => {
        if (activeTab === 5 && reviews.length === 0 && !reviewsLoading) {
            loadReviews();
        }
    }, [activeTab]);

    const loadReviews = async () => {
        setReviewsLoading(true);
        try {
            const response = await APIGetAdminReviews(token);
            if (response.success) {
                setReviews(response.reviews || []);
            }
        } catch (error) {
            console.error('Error loading reviews:', error);
        } finally {
            setReviewsLoading(false);
        }
    };

    const loadPlans = async () => {
        setPlansLoading(true);
        try {
            const response = await APIAdminGetPlans(token);
            if (response.success) {
                setPlans(response.plans || []);
            }
        } catch (error) {
            console.error('Error loading plans:', error);
        } finally {
            setPlansLoading(false);
        }
    };

    const handleOpenCreatePlan = () => {
        setEditingPlan(null);
        setPlanForm({
            title: '',
            description: '',
            cover_emoji: '',
            is_published: false,
            weeks: []
        });
        setPlanModalOpen(true);
    };

    const handleOpenEditPlan = (plan) => {
        setEditingPlan(plan);
        // We only have summary data from the list. We'll populate what we can,
        // and the user can modify weeks/steps from there.
        setPlanForm({
            title: plan.title || '',
            description: plan.description || '',
            cover_emoji: plan.cover_emoji || '',
            is_published: plan.is_published || false,
            weeks: plan.weeks || []
        });
        setPlanModalOpen(true);
    };

    const handleDeletePlan = async (plan) => {
        if (!window.confirm(`Удалить план "${plan.title}"? Это действие нельзя отменить!`)) return;

        try {
            const response = await APIAdminDeletePlan(token, plan.id);
            if (response.success) {
                setPlans(prev => prev.filter(p => p.id !== plan.id));
            } else {
                alert(response.error || 'Ошибка при удалении плана');
            }
        } catch (error) {
            console.error('Error deleting plan:', error);
            alert('Ошибка при удалении плана');
        }
    };

    const handleTogglePlanPublish = async (plan) => {
        try {
            const response = await APIAdminUpdatePlan(token, plan.id, {
                is_published: !plan.is_published
            });
            if (response.success) {
                setPlans(prev => prev.map(p =>
                    p.id === plan.id ? { ...p, is_published: !p.is_published } : p
                ));
            } else {
                alert(response.error || 'Ошибка при обновлении');
            }
        } catch (error) {
            console.error('Error toggling plan publish:', error);
            alert('Ошибка при обновлении плана');
        }
    };

    // Plan form helpers
    const handlePlanFormChange = (field, value) => {
        setPlanForm(prev => ({ ...prev, [field]: value }));
    };

    const handleAddWeek = () => {
        setPlanForm(prev => ({
            ...prev,
            weeks: [...prev.weeks, {
                week_number: prev.weeks.length + 1,
                title: `Неделя ${prev.weeks.length + 1}`,
                description: '',
                steps: []
            }]
        }));
    };

    const handleRemoveWeek = (weekIndex) => {
        setPlanForm(prev => ({
            ...prev,
            weeks: prev.weeks.filter((_, i) => i !== weekIndex).map((w, i) => ({
                ...w,
                week_number: i + 1
            }))
        }));
    };

    const handleWeekChange = (weekIndex, field, value) => {
        setPlanForm(prev => ({
            ...prev,
            weeks: prev.weeks.map((w, i) => i === weekIndex ? { ...w, [field]: value } : w)
        }));
    };

    const handleAddStep = (weekIndex) => {
        setPlanForm(prev => ({
            ...prev,
            weeks: prev.weeks.map((w, i) => {
                if (i !== weekIndex) return w;
                return {
                    ...w,
                    steps: [...w.steps, {
                        day_number: w.steps.length + 1,
                        title: '',
                        module_id: '',
                        category_id: '',
                        test_questions_count: 10,
                        lecture_url: '',
                        lecture_title: ''
                    }]
                };
            })
        }));
    };

    const handleRemoveStep = (weekIndex, stepIndex) => {
        setPlanForm(prev => ({
            ...prev,
            weeks: prev.weeks.map((w, i) => {
                if (i !== weekIndex) return w;
                return {
                    ...w,
                    steps: w.steps.filter((_, si) => si !== stepIndex)
                };
            })
        }));
    };

    const handleStepChange = (weekIndex, stepIndex, field, value) => {
        setPlanForm(prev => ({
            ...prev,
            weeks: prev.weeks.map((w, i) => {
                if (i !== weekIndex) return w;
                return {
                    ...w,
                    steps: w.steps.map((s, si) => si === stepIndex ? { ...s, [field]: value } : s)
                };
            })
        }));
    };

    const handleSavePlan = async () => {
        if (!planForm.title.trim()) {
            alert('Введите название плана');
            return;
        }

        setPlanSaving(true);
        try {
            // Clean up form data: convert empty strings to null for optional numeric fields
            const cleanedData = {
                ...planForm,
                weeks: planForm.weeks.map(w => ({
                    ...w,
                    steps: w.steps.map(s => ({
                        ...s,
                        module_id: s.module_id ? parseInt(s.module_id) : null,
                        category_id: s.category_id ? parseInt(s.category_id) : null,
                        test_questions_count: s.test_questions_count ? parseInt(s.test_questions_count) : 10,
                        day_number: s.day_number ? parseInt(s.day_number) : 1,
                        lecture_url: s.lecture_url || null,
                        lecture_title: s.lecture_title || null
                    }))
                }))
            };

            let response;
            if (editingPlan) {
                response = await APIAdminUpdatePlan(token, editingPlan.id, cleanedData);
            } else {
                response = await APIAdminCreatePlan(token, cleanedData);
            }

            if (response.success) {
                setPlanModalOpen(false);
                loadPlans();
            } else {
                alert(response.error || 'Ошибка при сохранении плана');
            }
        } catch (error) {
            console.error('Error saving plan:', error);
            alert('Ошибка при сохранении плана');
        } finally {
            setPlanSaving(false);
        }
    };

    if (user?.role !== 'admin') {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Alert color="danger" variant="soft">
                    <Typography level="h4">Доступ запрещен</Typography>
                    <Typography>У вас недостаточно прав для просмотра этой страницы</Typography>
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography level="h4">Загрузка...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
                {/* Заголовок */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography level="h2">Управление контентом</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="solid"
                            color="primary"
                            startDecorator={<Plus size={18} />}
                            onClick={() => handleCreateQuestion()}
                        >
                            Создать вопрос
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<Plus size={18} />}
                            onClick={() => handleCreateCategory()}
                        >
                            Создать категорию
                        </Button>
                    </Stack>
                </Box>

                {error && (
                    <Alert color="danger" variant="soft">
                        {error}
                    </Alert>
                )}

                {/* Помеченные вопросы */}
                {flaggedQuestions.length > 0 && (
                    <Alert
                        color="warning"
                        variant="soft"
                        endDecorator={
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="sm"
                                    variant="solid"
                                    color="danger"
                                    onClick={handleDeleteFlagged}
                                    loading={deletingFlagged}
                                >
                                    Удалить все ({flaggedQuestions.length})
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    onClick={handleClearFlags}
                                >
                                    Очистить флажки
                                </Button>
                            </Stack>
                        }
                    >
                        Помечено вопросов для удаления: {flaggedQuestions.length}
                    </Alert>
                )}

                {/* Табы */}
                <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
                    <TabList>
                        <Tab>Дерево категорий</Tab>
                        <Tab>Все вопросы ({allQuestions.length})</Tab>
                        <Tab>CSV Импорт</Tab>
                        <Tab>Дубликаты {duplicatesPagination?.total_count > 0 && `(${duplicatesPagination.total_count})`}</Tab>
                        <Tab>Планы {plans.length > 0 && `(${plans.length})`}</Tab>
                        <Tab>Отзывы {reviews.length > 0 && `(${reviews.length})`}</Tab>
                    </TabList>

                    <TabPanel value={0}>
                        {/* Дерево категорий */}
                        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2 }}>
                            <Typography level="h4" sx={{ mb: 2 }}>
                                Категории и вопросы
                            </Typography>
                            
                            {categories.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography level="body1" color="neutral">
                                        Категории не найдены. Создайте первую категорию.
                                    </Typography>
                                </Box>
                            ) : (
                                <AccordionGroup sx={{ gap: 1 }}>
                                    {categories.map((category) => (
                                        <CategoryAccordion
                                            key={category.id}
                                            category={category}
                                            level={0}
                                            questionsByCategory={questionsByCategory}
                                            onAddSubcategory={handleCreateCategory}
                                            onEditCategory={handleEditCategory}
                                            onDeleteCategory={handleDeleteCategory}
                                            onAddQuestion={handleCreateQuestion}
                                            onEditQuestion={handleEditQuestion}
                                            onDeleteQuestion={handleDeleteQuestion}
                                            onMoveQuestion={handleOpenMoveQuestion}
                                            getAllQuestionsInCategory={getAllQuestionsInCategory}
                                        />
                                    ))}
                                </AccordionGroup>
                            )}
                        </Sheet>
                    </TabPanel>

                    <TabPanel value={1}>
                        {/* Список всех вопросов */}
                        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography level="h4">
                                    Все вопросы
                                </Typography>

                                {/* Bulk action bar */}
                                {selectedQuestions.length > 0 && (
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Chip color="primary" variant="soft">
                                            Выбрано: {selectedQuestions.length}
                                        </Chip>
                                        <Select
                                            placeholder="Выберите категорию"
                                            value={bulkCategoryId}
                                            onChange={(e, value) => setBulkCategoryId(value)}
                                            size="sm"
                                            sx={{ minWidth: 200 }}
                                        >
                                            <Option value={null}>Убрать из всех категорий</Option>
                                            {flattenCategories(categories).map(cat => (
                                                <Option key={cat.id} value={cat.id}>
                                                    {cat.fullPath}
                                                </Option>
                                            ))}
                                        </Select>
                                        <Button
                                            size="sm"
                                            variant="solid"
                                            color="primary"
                                            onClick={handleBulkUpdateCategory}
                                            loading={bulkUpdating}
                                        >
                                            Применить
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="neutral"
                                            onClick={() => setSelectedQuestions([])}
                                        >
                                            Отменить
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="solid"
                                            color="danger"
                                            onClick={handleBulkDelete}
                                        >
                                            Удалить
                                        </Button>
                                    </Stack>
                                )}
                            </Box>

                            {questionsLoading ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography level="body1" color="neutral">
                                        Загрузка вопросов...
                                    </Typography>
                                </Box>
                            ) : allQuestions.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography level="body1" color="neutral">
                                        Вопросы не найдены. Создайте первый вопрос.
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={2}>
                                    {/* Select All */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Checkbox
                                            checked={selectedQuestions.length === allQuestions.length && allQuestions.length > 0}
                                            indeterminate={selectedQuestions.length > 0 && selectedQuestions.length < allQuestions.length}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                        />
                                        <Typography level="body-sm" fontWeight="md">
                                            Выбрать все ({allQuestions.length})
                                        </Typography>
                                    </Box>

                                    {allQuestions.map((question) => (
                                        <QuestionItem
                                            key={question.id}
                                            question={question}
                                            onEdit={handleEditQuestion}
                                            onDelete={handleDeleteQuestion}
                                            selected={selectedQuestions.includes(question.id)}
                                            onSelect={handleSelectQuestion}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Sheet>
                    </TabPanel>

                    <TabPanel value={2}>
                        {/* CSV Import */}
                        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 3 }}>
                            <Typography level="h4" sx={{ mb: 3 }}>
                                Импорт вопросов из CSV
                            </Typography>

                            <Stack spacing={3}>
                                {/* Описание формата */}
                                <Alert color="neutral" variant="soft">
                                    <Typography level="body-sm">
                                        <strong>Формат CSV файла (9 колонок):</strong>
                                    </Typography>
                                    <List size="sm" sx={{ mt: 1 }}>
                                        <ListItem>1. ID вопроса (внешний идентификатор)</ListItem>
                                        <ListItem>2. Текст вопроса</ListItem>
                                        <ListItem>3-7. Варианты ответов A, B, C, D, E</ListItem>
                                        <ListItem>8. Правильный ответ (A, B, C, D, E или несколько через запятую)</ListItem>
                                        <ListItem>9. Объяснение ответа</ListItem>
                                    </List>
                                </Alert>

                                {/* Выбор файла */}
                                <FormControl>
                                    <FormLabel>CSV файл</FormLabel>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleCsvFileSelect}
                                        ref={csvFileInputRef}
                                        style={{
                                            padding: '12px',
                                            border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                                            borderRadius: '8px',
                                            backgroundColor: 'var(--joy-palette-background-surface)'
                                        }}
                                    />
                                    {csvFile && (
                                        <Typography level="body-sm" sx={{ mt: 1 }}>
                                            Выбран файл: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(1)} KB)
                                        </Typography>
                                    )}
                                </FormControl>

                                {/* Выбор категории */}
                                <FormControl>
                                    <FormLabel>Категория (опционально)</FormLabel>
                                    <Select
                                        placeholder="Выберите категорию для всех вопросов"
                                        value={csvImportCategory}
                                        onChange={(e, value) => setCsvImportCategory(value)}
                                    >
                                        <Option value={null}>Без категории</Option>
                                        {flattenCategories(categories).map(cat => (
                                            <Option key={cat.id} value={cat.id}>
                                                {cat.fullPath}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Кнопка импорта */}
                                <Button
                                    variant="solid"
                                    color="primary"
                                    size="lg"
                                    startDecorator={<UploadSimple size={18} />}
                                    onClick={handleCsvImport}
                                    disabled={!csvFile || csvImporting}
                                    loading={csvImporting}
                                >
                                    {csvImporting ? 'Импортирую...' : 'Импортировать'}
                                </Button>

                                {/* Прогресс */}
                                {csvImporting && (
                                    <LinearProgress />
                                )}

                                {/* Результат импорта */}
                                {csvImportResult && (
                                    <Alert
                                        color={csvImportResult.success ? 'success' : 'danger'}
                                        variant="soft"
                                        startDecorator={csvImportResult.success ? <CheckCircle size={20} weight="fill" /> : <WarningCircle size={20} />}
                                    >
                                        <Box>
                                            <Typography level="title-md">
                                                {csvImportResult.success ? 'Импорт завершен' : 'Ошибка импорта'}
                                            </Typography>
                                            <Typography level="body-sm">
                                                {csvImportResult.message || csvImportResult.error}
                                            </Typography>

                                            {csvImportResult.success && csvImportResult.created_count > 0 && (
                                                <Typography level="body-sm" sx={{ mt: 1 }}>
                                                    Создано вопросов: <strong>{csvImportResult.created_count}</strong>
                                                </Typography>
                                            )}

                                            {csvImportResult.errors && csvImportResult.errors.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography level="body-sm" fontWeight="bold">
                                                        Ошибки ({csvImportResult.errors.length}):
                                                    </Typography>
                                                    <List size="sm" sx={{ maxHeight: 200, overflow: 'auto' }}>
                                                        {csvImportResult.errors.map((err, idx) => (
                                                            <ListItem key={idx}>
                                                                <ListItemContent>
                                                                    <Typography level="body-xs" color="danger">
                                                                        {err}
                                                                    </Typography>
                                                                </ListItemContent>
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                        </Box>
                                    </Alert>
                                )}
                            </Stack>
                        </Sheet>
                    </TabPanel>

                    <TabPanel value={3}>
                        {/* Анализ дубликатов */}
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                            {/* Левая панель - выбор категорий */}
                            <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2, flex: 1, maxWidth: { md: 350 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography level="title-md">
                                        Выберите категории
                                    </Typography>
                                    {analyzedCategories.length > 0 && (
                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            onClick={handleClearAnalyzedCategories}
                                        >
                                            Сбросить ✓
                                        </Button>
                                    )}
                                </Box>

                                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                    {categories.map((cat) => (
                                        <DuplicateCategoryItem
                                            key={cat.id}
                                            category={cat}
                                            level={0}
                                            expandedDupCategories={expandedDupCategories}
                                            setExpandedDupCategories={setExpandedDupCategories}
                                            selectedCategoriesForAnalysis={selectedCategoriesForAnalysis}
                                            handleToggleCategoryForAnalysis={handleToggleCategoryForAnalysis}
                                            analyzedCategories={analyzedCategories}
                                            analyzingDuplicates={analyzingDuplicates}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button
                                        fullWidth
                                        variant="solid"
                                        color="primary"
                                        onClick={handleAnalyzeDuplicates}
                                        loading={analyzingDuplicates}
                                        disabled={selectedCategoriesForAnalysis.length === 0}
                                        startDecorator={<Exam size={18} />}
                                    >
                                        {analyzingDuplicates ? 'Анализ...' : `Анализировать (${selectedCategoriesForAnalysis.length})`}
                                    </Button>
                                </Box>
                            </Sheet>

                            {/* Правая панель - результаты */}
                            <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2, flex: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography level="h4">
                                        Найденные дубликаты
                                        {duplicatesPagination?.total_count > 0 && (
                                            <Chip size="sm" color="warning" variant="soft" sx={{ ml: 1 }}>
                                                {duplicatesPagination.total_count}
                                            </Chip>
                                        )}
                                    </Typography>
                                    {duplicatesPagination?.total_count > 0 && (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="sm"
                                                variant="soft"
                                                color="neutral"
                                                onClick={handleClearDuplicateFlags}
                                            >
                                                Снять метки
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="soft"
                                                color="danger"
                                                onClick={handleHideAllDuplicates}
                                            >
                                                Скрыть все
                                            </Button>
                                        </Stack>
                                    )}
                                </Box>

                                {duplicatesError && (
                                    <Alert color="danger" sx={{ mb: 2 }}>
                                        {duplicatesError}
                                    </Alert>
                                )}

                                {analyzingDuplicates && (
                                    <Alert color="primary" sx={{ mb: 2 }}>
                                        <Stack spacing={1}>
                                            <Typography>Идёт анализ вопросов через Gemini AI...</Typography>
                                            <Typography level="body-sm">Это может занять несколько минут.</Typography>
                                            <LinearProgress />
                                        </Stack>
                                    </Alert>
                                )}

                                {duplicatesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : duplicates.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography level="body1" color="neutral">
                                            {analyzedCategories.length > 0
                                                ? 'Дубликаты не найдены в проанализированных категориях.'
                                                : 'Выберите категории и запустите анализ.'}
                                        </Typography>
                                    </Box>
                                ) : (
                                <Stack spacing={2}>
                                    {duplicates.map((dup) => (
                                        <Sheet
                                            key={dup.id}
                                            variant="soft"
                                            color="warning"
                                            sx={{ p: 2, borderRadius: 'md' }}
                                        >
                                            <Stack spacing={1.5}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                            <Chip size="sm" color="danger" variant="soft">
                                                                #{dup.id} - Дубликат
                                                            </Chip>
                                                            {dup.categories?.map((cat, idx) => (
                                                                <Chip key={idx} size="sm" variant="outlined">
                                                                    {cat}
                                                                </Chip>
                                                            ))}
                                                        </Stack>
                                                        <Typography level="body-md" fontWeight="lg">
                                                            {dup.text}
                                                        </Typography>
                                                        {dup.description && (
                                                            <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                                {dup.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Stack direction="row" spacing={1}>
                                                        <IconButton
                                                            size="sm"
                                                            variant="soft"
                                                            color="neutral"
                                                            onClick={() => handleUnmarkDuplicate(dup.id)}
                                                            title="Снять метку"
                                                        >
                                                            <CheckCircle size={18} weight="fill" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="sm"
                                                            variant="soft"
                                                            color="warning"
                                                            onClick={() => handleHideDuplicate(dup.id)}
                                                            title="Скрыть вопрос"
                                                        >
                                                            <EyeSlash size={18} />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>

                                                {/* Причина */}
                                                {dup.duplicate_reason && (
                                                    <Alert size="sm" color="neutral" variant="soft">
                                                        <Typography level="body-sm">
                                                            <strong>Причина:</strong> {dup.duplicate_reason}
                                                        </Typography>
                                                    </Alert>
                                                )}

                                                {/* Оригинал */}
                                                {dup.duplicate_of && (
                                                    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm', bgcolor: 'background.surface' }}>
                                                        <Typography level="body-xs" color="success" fontWeight="lg" sx={{ mb: 0.5 }}>
                                                            Оригинал (оставить): #{dup.duplicate_of.id}
                                                        </Typography>
                                                        <Typography level="body-sm">
                                                            {dup.duplicate_of.text}
                                                        </Typography>
                                                    </Sheet>
                                                )}
                                            </Stack>
                                        </Sheet>
                                    ))}

                                    {/* Pagination */}
                                    {duplicatesPagination && duplicatesPagination.total_pages > 1 && (
                                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                            <Button
                                                size="sm"
                                                variant="soft"
                                                disabled={!duplicatesPagination.has_previous}
                                                onClick={() => loadDuplicates(duplicatesPagination.current_page - 1)}
                                            >
                                                Назад
                                            </Button>
                                            <Typography level="body-sm" sx={{ alignSelf: 'center' }}>
                                                {duplicatesPagination.current_page} / {duplicatesPagination.total_pages}
                                            </Typography>
                                            <Button
                                                size="sm"
                                                variant="soft"
                                                disabled={!duplicatesPagination.has_next}
                                                onClick={() => loadDuplicates(duplicatesPagination.current_page + 1)}
                                            >
                                                Вперёд
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        </Sheet>
                        </Box>
                    </TabPanel>

                    <TabPanel value={4}>
                        {/* Управление планами */}
                        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography level="h4">Учебные планы</Typography>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    startDecorator={<Plus size={18} />}
                                    onClick={handleOpenCreatePlan}
                                >
                                    Создать план
                                </Button>
                            </Box>

                            {plansLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : plans.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography level="body-md" color="neutral">
                                        Планы не найдены. Создайте первый учебный план.
                                    </Typography>
                                </Box>
                            ) : (
                                <Table
                                    stripe="odd"
                                    hoverRow
                                    sx={{
                                        '& th': { fontWeight: 600 },
                                        '& td': { verticalAlign: 'middle' }
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={{ width: '30%' }}>Название</th>
                                            <th style={{ width: '10%', textAlign: 'center' }}>Недели</th>
                                            <th style={{ width: '10%', textAlign: 'center' }}>Шаги</th>
                                            <th style={{ width: '12%', textAlign: 'center' }}>Студенты</th>
                                            <th style={{ width: '15%', textAlign: 'center' }}>Статус</th>
                                            <th style={{ width: '23%', textAlign: 'center' }}>Действия</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {plans.map((plan) => (
                                            <tr key={plan.id}>
                                                <td>
                                                    <Stack spacing={0.5}>
                                                        <Typography level="body-md" fontWeight="lg">
                                                            {plan.cover_emoji && `${plan.cover_emoji} `}{plan.title}
                                                        </Typography>
                                                        {plan.description && (
                                                            <Typography level="body-xs" color="neutral">
                                                                {plan.description.length > 80
                                                                    ? plan.description.substring(0, 80) + '...'
                                                                    : plan.description}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Chip size="sm" variant="soft">{plan.weeks_count || 0}</Chip>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Chip size="sm" variant="soft">{plan.steps_count || 0}</Chip>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Chip size="sm" variant="soft" color="primary">
                                                        {plan.enrollments_count || 0}
                                                    </Chip>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Chip
                                                        size="sm"
                                                        variant="soft"
                                                        color={plan.is_published ? 'success' : 'neutral'}
                                                        onClick={() => handleTogglePlanPublish(plan)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        {plan.is_published ? 'Опубликован' : 'Черновик'}
                                                    </Chip>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                                        <IconButton
                                                            size="sm"
                                                            variant="soft"
                                                            color="primary"
                                                            onClick={() => handleOpenEditPlan(plan)}
                                                            title="Редактировать"
                                                        >
                                                            <PencilSimple size={18} />
                                                        </IconButton>
                                                        <IconButton
                                                            size="sm"
                                                            variant="soft"
                                                            color="danger"
                                                            onClick={() => handleDeletePlan(plan)}
                                                            title="Удалить"
                                                        >
                                                            <Trash size={18} />
                                                        </IconButton>
                                                    </Stack>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Sheet>
                    </TabPanel>

                    <TabPanel value={5}>
                        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography level="title-lg">Отзывы пользователей</Typography>
                                <Button size="sm" variant="outlined" onClick={loadReviews} loading={reviewsLoading}>
                                    Обновить
                                </Button>
                            </Stack>

                            {reviewsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : reviews.length === 0 ? (
                                <Typography level="body-md" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                    Пока нет отзывов
                                </Typography>
                            ) : (
                                <Stack spacing={2}>
                                    {reviews.map((review) => (
                                        <Sheet key={review.id} variant="soft" sx={{ borderRadius: 'md', p: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                                                <Box>
                                                    <Typography level="title-sm">{review.user_name || review.user_email}</Typography>
                                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{review.user_email}</Typography>
                                                </Box>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Chip size="sm" variant="soft" color="neutral">{review.course}</Chip>
                                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>{review.created_at}</Typography>
                                                </Stack>
                                            </Stack>

                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <Star key={star} size={18} weight={star <= review.rating ? "fill" : "regular"} color={star <= review.rating ? '#F59E0B' : '#D1D5DB'} />
                                                ))}
                                                <Chip size="sm" variant="soft" color={review.would_recommend === 'Да' ? 'success' : review.would_recommend === 'Скорее да' ? 'warning' : 'danger'} sx={{ ml: 1 }}>
                                                    Рекомендует: {review.would_recommend}
                                                </Chip>
                                            </Stack>

                                            <Divider sx={{ my: 1 }} />

                                            <Stack spacing={1}>
                                                <Box>
                                                    <Typography level="body-xs" sx={{ fontWeight: 600, color: 'success.plainColor' }}>Что нравится:</Typography>
                                                    <Typography level="body-sm">{review.liked_most}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography level="body-xs" sx={{ fontWeight: 600, color: 'warning.plainColor' }}>Что улучшить:</Typography>
                                                    <Typography level="body-sm">{review.improvement}</Typography>
                                                </Box>
                                                {review.wishes && (
                                                    <Box>
                                                        <Typography level="body-xs" sx={{ fontWeight: 600, color: 'primary.plainColor' }}>Пожелания:</Typography>
                                                        <Typography level="body-sm">{review.wishes}</Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Sheet>
                                    ))}
                                </Stack>
                            )}
                        </Sheet>
                    </TabPanel>
                </Tabs>
            </Stack>

            {/* Модальное окно для создания/редактирования плана */}
            <Modal
                open={planModalOpen}
                onClose={() => setPlanModalOpen(false)}
            >
                <ModalDialog
                    sx={{
                        width: '90vw',
                        maxWidth: 800,
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}
                >
                    <ModalClose />
                    <DialogTitle>
                        {editingPlan ? `Редактировать план: ${editingPlan.title}` : 'Создать учебный план'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2.5} sx={{ mt: 1 }}>
                            {/* Plan basic fields */}
                            <FormControl>
                                <FormLabel>Название</FormLabel>
                                <Input
                                    value={planForm.title}
                                    onChange={(e) => handlePlanFormChange('title', e.target.value)}
                                    placeholder="Например: USMLE Step 1 - 4 недели"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Описание</FormLabel>
                                <Textarea
                                    value={planForm.description}
                                    onChange={(e) => handlePlanFormChange('description', e.target.value)}
                                    placeholder="Описание плана..."
                                    minRows={2}
                                    maxRows={4}
                                />
                            </FormControl>

                            <Stack direction="row" spacing={2}>
                                <FormControl sx={{ flex: 1 }}>
                                    <FormLabel>Эмодзи обложки</FormLabel>
                                    <Input
                                        value={planForm.cover_emoji}
                                        onChange={(e) => handlePlanFormChange('cover_emoji', e.target.value)}
                                        placeholder="e.g. 📚"
                                        sx={{ maxWidth: 120 }}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Опубликован</FormLabel>
                                    <Switch
                                        checked={planForm.is_published}
                                        onChange={(e) => handlePlanFormChange('is_published', e.target.checked)}
                                        sx={{ mt: 0.5 }}
                                    />
                                </FormControl>
                            </Stack>

                            <Divider />

                            {/* Weeks section */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography level="title-lg">
                                        Недели ({planForm.weeks.length})
                                    </Typography>
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        color="primary"
                                        startDecorator={<Plus size={18} />}
                                        onClick={handleAddWeek}
                                    >
                                        Добавить неделю
                                    </Button>
                                </Box>

                                {planForm.weeks.length === 0 && (
                                    <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 2 }}>
                                        Нет недель. Нажмите "Добавить неделю" чтобы начать.
                                    </Typography>
                                )}

                                <Stack spacing={2}>
                                    {planForm.weeks.map((week, weekIndex) => (
                                        <Sheet
                                            key={weekIndex}
                                            variant="outlined"
                                            sx={{ p: 2, borderRadius: 'md' }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                <Typography level="title-md">
                                                    Неделя {week.week_number}
                                                </Typography>
                                                <IconButton
                                                    size="sm"
                                                    variant="soft"
                                                    color="danger"
                                                    onClick={() => handleRemoveWeek(weekIndex)}
                                                    title="Удалить неделю"
                                                >
                                                    <Trash size={18} />
                                                </IconButton>
                                            </Box>

                                            <Stack spacing={1.5}>
                                                <Stack direction="row" spacing={1}>
                                                    <FormControl sx={{ flex: 1 }}>
                                                        <FormLabel>Название недели</FormLabel>
                                                        <Input
                                                            size="sm"
                                                            value={week.title}
                                                            onChange={(e) => handleWeekChange(weekIndex, 'title', e.target.value)}
                                                            placeholder="Например: Биохимия"
                                                        />
                                                    </FormControl>
                                                    <FormControl sx={{ flex: 1 }}>
                                                        <FormLabel>Описание</FormLabel>
                                                        <Input
                                                            size="sm"
                                                            value={week.description || ''}
                                                            onChange={(e) => handleWeekChange(weekIndex, 'description', e.target.value)}
                                                            placeholder="Краткое описание"
                                                        />
                                                    </FormControl>
                                                </Stack>

                                                {/* Steps */}
                                                <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.200' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography level="body-sm" fontWeight="lg">
                                                            Шаги ({week.steps.length})
                                                        </Typography>
                                                        <Button
                                                            size="sm"
                                                            variant="plain"
                                                            color="primary"
                                                            startDecorator={<Plus size={18} />}
                                                            onClick={() => handleAddStep(weekIndex)}
                                                        >
                                                            Добавить шаг
                                                        </Button>
                                                    </Box>

                                                    <Stack spacing={1}>
                                                        {week.steps.map((step, stepIndex) => (
                                                            <Sheet
                                                                key={stepIndex}
                                                                variant="soft"
                                                                sx={{ p: 1.5, borderRadius: 'sm' }}
                                                            >
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                                    <Typography level="body-xs" fontWeight="lg" color="primary">
                                                                        Шаг {stepIndex + 1}
                                                                    </Typography>
                                                                    <IconButton
                                                                        size="sm"
                                                                        variant="plain"
                                                                        color="danger"
                                                                        onClick={() => handleRemoveStep(weekIndex, stepIndex)}
                                                                        sx={{ '--IconButton-size': '24px' }}
                                                                    >
                                                                        <Trash size={16} />
                                                                    </IconButton>
                                                                </Box>

                                                                <Stack spacing={1}>
                                                                    <Stack direction="row" spacing={1}>
                                                                        <FormControl sx={{ width: 80 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>День</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                type="number"
                                                                                value={step.day_number}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'day_number', e.target.value)}
                                                                            />
                                                                        </FormControl>
                                                                        <FormControl sx={{ flex: 1 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>Название шага</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                value={step.title}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'title', e.target.value)}
                                                                                placeholder="Название шага"
                                                                            />
                                                                        </FormControl>
                                                                    </Stack>

                                                                    <Stack direction="row" spacing={1}>
                                                                        <FormControl sx={{ width: 100 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>Module ID</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                type="number"
                                                                                value={step.module_id || ''}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'module_id', e.target.value)}
                                                                                placeholder="ID"
                                                                            />
                                                                        </FormControl>
                                                                        <FormControl sx={{ width: 100 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>Category ID</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                type="number"
                                                                                value={step.category_id || ''}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'category_id', e.target.value)}
                                                                                placeholder="ID"
                                                                            />
                                                                        </FormControl>
                                                                        <FormControl sx={{ width: 80 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>Вопросов</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                type="number"
                                                                                value={step.test_questions_count || 10}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'test_questions_count', e.target.value)}
                                                                            />
                                                                        </FormControl>
                                                                    </Stack>

                                                                    <Stack direction="row" spacing={1}>
                                                                        <FormControl sx={{ flex: 1 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>Лекция URL</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                value={step.lecture_url || ''}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'lecture_url', e.target.value)}
                                                                                placeholder="https://..."
                                                                            />
                                                                        </FormControl>
                                                                        <FormControl sx={{ flex: 1 }}>
                                                                            <FormLabel sx={{ fontSize: '0.7rem' }}>Название лекции</FormLabel>
                                                                            <Input
                                                                                size="sm"
                                                                                value={step.lecture_title || ''}
                                                                                onChange={(e) => handleStepChange(weekIndex, stepIndex, 'lecture_title', e.target.value)}
                                                                                placeholder="Название лекции"
                                                                            />
                                                                        </FormControl>
                                                                    </Stack>
                                                                </Stack>
                                                            </Sheet>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Sheet>
                                    ))}
                                </Stack>
                            </Box>

                            <Divider />

                            {/* Save/Cancel buttons */}
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => setPlanModalOpen(false)}
                                    disabled={planSaving}
                                >
                                    Отмена
                                </Button>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleSavePlan}
                                    loading={planSaving}
                                >
                                    {editingPlan ? 'Сохранить' : 'Создать план'}
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* Модальное окно для редактирования категории */}
            <CategoryModal
                open={categoryModalOpen}
                category={editingCategory}
                parentCategory={parentCategory}
                onClose={() => setCategoryModalOpen(false)}
                onSave={handleSaveCategory}
            />

            {/* Модальное окно для редактирования вопроса */}
            <QuestionModal
                open={questionModalOpen}
                category={selectedCategory}
                question={editingQuestion}
                onClose={() => setQuestionModalOpen(false)}
                onSave={handleSaveQuestion}
                token={token}
            />

            {/* Модальное окно переноса вопроса */}
            <Modal
                open={moveQuestionModalOpen}
                onClose={() => setMoveQuestionModalOpen(false)}
            >
                <ModalDialog sx={{ width: 450 }}>
                    <ModalClose />
                    <DialogTitle>Переместить вопрос</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            {questionToMove && (
                                <Typography level="body-sm" sx={{
                                    p: 1.5,
                                    bgcolor: 'background.level1',
                                    borderRadius: 'md'
                                }}>
                                    {questionToMove.text?.substring(0, 150)}
                                    {questionToMove.text?.length > 150 ? '...' : ''}
                                </Typography>
                            )}

                            <FormControl>
                                <FormLabel>Выберите категорию</FormLabel>
                                <Select
                                    value={targetCategoryId}
                                    onChange={(e, value) => setTargetCategoryId(value)}
                                    placeholder="Выберите категорию..."
                                >
                                    {flatCategories.map(cat => (
                                        <Option key={cat.id} value={cat.id}>
                                            {cat.displayName}
                                        </Option>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => setMoveQuestionModalOpen(false)}
                                    disabled={movingQuestion}
                                >
                                    Отмена
                                </Button>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={handleMoveQuestion}
                                    disabled={!targetCategoryId}
                                    loading={movingQuestion}
                                >
                                    Переместить
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>
        </Box>
    );
}

// Компонент категории для выбора дубликатов (с раскрытием)
function DuplicateCategoryItem({
    category,
    level,
    expandedDupCategories,
    setExpandedDupCategories,
    selectedCategoriesForAnalysis,
    handleToggleCategoryForAnalysis,
    analyzedCategories,
    analyzingDuplicates
}) {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedDupCategories.includes(category.id);
    const isSelected = selectedCategoriesForAnalysis.includes(category.id);
    const isAnalyzed = analyzedCategories.includes(category.id);

    const toggleExpand = () => {
        setExpandedDupCategories(prev =>
            prev.includes(category.id)
                ? prev.filter(id => id !== category.id)
                : [...prev, category.id]
        );
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.5,
                    pl: level * 2,
                    '&:hover': { bgcolor: 'background.level1' },
                    borderRadius: 'sm'
                }}
            >
                {/* Кнопка раскрытия */}
                {hasChildren ? (
                    <IconButton
                        size="sm"
                        variant="plain"
                        onClick={toggleExpand}
                        sx={{ mr: 0.5, minWidth: 24 }}
                    >
                        {isExpanded ? <CaretDown size={18} /> : <CaretRight size={18} />}
                    </IconButton>
                ) : (
                    <Box sx={{ width: 32 }} />
                )}

                <Checkbox
                    size="sm"
                    checked={isSelected}
                    onChange={() => handleToggleCategoryForAnalysis(category.id)}
                    disabled={analyzingDuplicates}
                />
                <Typography
                    level="body-sm"
                    sx={{
                        ml: 1,
                        flex: 1,
                        fontWeight: hasChildren ? 600 : 400,
                        color: isAnalyzed ? 'success.600' : 'text.primary',
                        cursor: hasChildren ? 'pointer' : 'default'
                    }}
                    onClick={() => hasChildren && toggleExpand()}
                >
                    {category.name}
                </Typography>
                {isAnalyzed && (
                    <CheckCircle size={16} weight="fill" style={{ marginRight: 8 }} />
                )}
                <Chip size="sm" variant="soft">
                    {category.question_count || 0}
                </Chip>
            </Box>

            {/* Дочерние категории */}
            {hasChildren && isExpanded && (
                <Box>
                    {category.children.map(child => (
                        <DuplicateCategoryItem
                            key={child.id}
                            category={child}
                            level={level + 1}
                            expandedDupCategories={expandedDupCategories}
                            setExpandedDupCategories={setExpandedDupCategories}
                            selectedCategoriesForAnalysis={selectedCategoriesForAnalysis}
                            handleToggleCategoryForAnalysis={handleToggleCategoryForAnalysis}
                            analyzedCategories={analyzedCategories}
                            analyzingDuplicates={analyzingDuplicates}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

// Компонент аккордиона категории
function CategoryAccordion({
    category,
    level,
    questionsByCategory,
    onAddSubcategory,
    onEditCategory,
    onDeleteCategory,
    onAddQuestion,
    onEditQuestion,
    onDeleteQuestion,
    onMoveQuestion,
    getAllQuestionsInCategory
}) {
    const [expanded, setExpanded] = useState(level === 0); // Автоматически раскрываем корневые категории
    const [questionsExpanded, setQuestionsExpanded] = useState(false); // Список вопросов свернут по умолчанию

    const hasChildren = category.children && category.children.length > 0;
    const directQuestions = questionsByCategory[category.id] || [];
    const allQuestionsInCategory = getAllQuestionsInCategory(category);

    return (
        <Accordion
            expanded={expanded}
            variant='outlined'
            onChange={(event, isExpanded) => setExpanded(isExpanded)}
            sx={{
                '&:before': { display: 'none' },
                borderRadius: 'md',
                ml: level * 2
            }}
        >
            <AccordionSummary
                indicator={<CaretDown size={20} />}
                sx={{
                    borderRadius: expanded ? 'md md 0 0' : 'md'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
                    <Folder size={20} />
                    
                    <Box sx={{ flex: 1 }}>
                        <Typography level="title-md">{category.name}</Typography>
                        {category.description && (
                            <Typography level="body2" color="neutral">
                                {category.description}
                            </Typography>
                        )}
                    </Box>

                    {/* Статус и счетчики */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        {!category.is_active && (
                            <Chip size="sm" color="neutral" variant="soft">
                                Неактивна
                            </Chip>
                        )}
                        
                        <Chip size="sm" variant="outlined">
                            {category.question_count} вопросов
                        </Chip>

                        {/* Кнопки действий */}
                        <Stack direction="row" spacing={0.5}>
                            <Button
                                size="sm"
                                variant="outlined"
                                color="primary"
                                startDecorator={<Plus size={18} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddSubcategory(category);
                                }}
                            >
                                Категорию
                            </Button>

                            <Button
                                size="sm"
                                variant="soft"
                                color="primary"
                                startDecorator={<Plus size={18} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAddQuestion(category);
                                }}
                            >
                                Вопрос
                            </Button>
                            
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="neutral"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEditCategory(category);
                                }}
                            >
                                <PencilSimple size={18} />
                            </IconButton>
                            
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteCategory(category);
                                }}
                                title="Удалить категорию"
                            >
                                <Trash size={18} />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Stack spacing={2}>
                    {/* Подкатегории */}
                    {hasChildren && (
                        <Box>
                            <Typography level="body2" fontWeight="bold" sx={{ mb: 1 }}>
                                Подкатегории:
                            </Typography>
                            <AccordionGroup>
                                {category.children.map((childCategory) => (
                                    <CategoryAccordion
                                        key={childCategory.id}
                                        category={childCategory}
                                        level={level + 1}
                                        questionsByCategory={questionsByCategory}
                                        onAddSubcategory={onAddSubcategory}
                                        onEditCategory={onEditCategory}
                                        onDeleteCategory={onDeleteCategory}
                                        onAddQuestion={onAddQuestion}
                                        onEditQuestion={onEditQuestion}
                                        onDeleteQuestion={onDeleteQuestion}
                                        onMoveQuestion={onMoveQuestion}
                                        getAllQuestionsInCategory={getAllQuestionsInCategory}
                                    />
                                ))}
                            </AccordionGroup>
                        </Box>
                    )}

                    {/* Вопросы в этой категории */}
                    {directQuestions.length > 0 && (
                        <Box>
                            <Box
                                onClick={() => setQuestionsExpanded(!questionsExpanded)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 'sm',
                                    '&:hover': {
                                        bgcolor: 'background.level1'
                                    }
                                }}
                            >
                                <CaretDown
                                    size={18}
                                    style={{
                                        transition: 'transform 0.2s',
                                        transform: questionsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                                    }}
                                />
                                <Typography level="body-sm" fontWeight="bold">
                                    Вопросы в этой категории ({directQuestions.length})
                                </Typography>
                            </Box>
                            {questionsExpanded && (
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                    {directQuestions.map((question) => (
                                        <QuestionItem
                                            key={question.id}
                                            question={question}
                                            onEdit={onEditQuestion}
                                            onDelete={onDeleteQuestion}
                                            onMove={onMoveQuestion}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {/* Сообщение если нет ни вопросов ни подкатегорий */}
                    {!hasChildren && directQuestions.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <Typography level="body2" color="neutral">
                                Нет подкатегорий и вопросов
                            </Typography>
                            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    startDecorator={<Plus size={18} />}
                                    onClick={() => onAddQuestion(category)}
                                >
                                    Добавить вопрос
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    startDecorator={<Plus size={18} />}
                                    onClick={() => onAddSubcategory(category)}
                                >
                                    Добавить подкатегорию
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}

// Компонент для отображения вопроса
function QuestionItem({ question, onEdit, onDelete, onMove, selected, onSelect }) {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await onDelete(question);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Sheet
            variant="soft"
            sx={{
                p: 2,
                borderRadius: 'md',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                bgcolor: selected ? 'primary.softBg' : undefined,
                border: selected ? '2px solid' : '2px solid transparent',
                borderColor: selected ? 'primary.500' : 'transparent',
            }}
        >
            {onSelect && (
                <Checkbox
                    checked={selected}
                    onChange={(e) => onSelect(question.id, e.target.checked)}
                    sx={{ mt: 0.5 }}
                />
            )}
            <Exam size={18} />
            
            <Box sx={{ flex: 1 }}>
                <Typography level="body1" fontWeight="bold">
                    {question.text}
                </Typography>
                
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip size="sm" variant="soft">
                        {question.type === 'single' ? 'Один вариант' : 
                         question.type === 'multiple' ? 'Несколько вариантов' : 'Текстовый ответ'}
                    </Chip>
                    
                    <Chip size="sm" variant="outlined">
                        {question.answers?.length || 0} ответов
                    </Chip>

                    <Chip 
                        size="sm" 
                        variant={question.is_active ? 'soft' : 'outlined'}
                        color={question.is_active ? 'success' : 'neutral'}
                    >
                        {question.is_active ? 'Активен' : 'Неактивен'}
                    </Chip>

                    {question.categories && question.categories.length > 0 && (
                        <Chip size="sm" variant="soft" color="primary">
                            {question.categories.length} кат.
                        </Chip>
                    )}
                </Stack>

                {/* Категории вопроса */}
                {question.categories && question.categories.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography level="body3" color="neutral">
                            Категории: {question.categories.map(cat => cat.name).join(', ')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Stack direction="row" spacing={0.5}>
                <IconButton
                    size="sm"
                    variant="soft"
                    color="primary"
                    onClick={() => onEdit(question)}
                    title="Редактировать"
                >
                    <PencilSimple size={18} />
                </IconButton>
                {onMove && (
                    <IconButton
                        size="sm"
                        variant="soft"
                        color="neutral"
                        onClick={() => onMove(question)}
                        title="Переместить в другую категорию"
                    >
                        <FolderOpen size={18} />
                    </IconButton>
                )}
                <IconButton
                    size="sm"
                    variant="soft"
                    color="danger"
                    onClick={handleDelete}
                    disabled={deleting}
                    title="Удалить вопрос"
                >
                    {deleting ? <CircularProgress size="sm" /> : <Trash size={18} />}
                </IconButton>
            </Stack>
        </Sheet>
    );
}